import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import MoleculeAuthInput from './molecules/MoleculeAuthInput';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { AlertPosition, AlertType, ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { useValidation } from '../../shared/hooks/useValidation';
import SearchForgot from './SearchForgot';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';


interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const Login: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { login } = useAuth();
  const { isLoading } = useLoadingStore();
  const { clearModal, setStandardModal, standardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isEmail } = useValidation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (email.length > 0 || password.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [email, password]);

  useEffect(() => {
    const inputElement = document.querySelector('input') as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
    // if (modalRef.current) {
    //   modalRef.current.focus();
    // }
  }, []);

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  }

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError('');
      return true;
    }
    setPasswordError('');
    return true;
  };

  const changePasswordInput = (value: string) => {
    validatePassword(value);
    setPassword(value);
  }

  const isEmpty = () => {
    return (email === '' || password === '')
  }

  const isValid = () => {
    return (!emailError && !passwordError)
  }

  const handleSubmit = async () => {
    if (isEmpty()) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD')
      })
      return;
    }
    if (!isValid()) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT')
      })
      return;
    }
    await login({ email, password });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) {
      return null;
    }
    switch (event.key) {
      case 'Enter':
        if (event.target instanceof HTMLButtonElement) return;
        handleSubmit();
        break;
      case 'Escape':
        clearModal();
        break;
      default:
        break;
    }
  };

  const openRegisterModal = () => {
    setStandardModal({
      modalType: ModalType.REGISTER,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const moveToHome = () => {
    window.location.href = "/";
  }

  return (
    <LoginComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <HeaderRow>
        <Title>{getModalMessage('LOGIN_TITLE')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => standardModal?.option ? moveToHome() : clearModal() }
        />
      </HeaderRow>
      <MoleculeAuthInput
        name={getModalMessage('AUTH_EMAIL_LABEL')}
        value={email}
        handleChange={changeEmailInput}
        err={emailError}
        placeholder={getModalMessage('AUTH_EMAIL_LABEL')}
        nameVisible={false}
        errVisible={false}
      />
      <MoleculeAuthInput
        name={getModalMessage('AUTH_PASSWORD_LABEL')}
        value={password}
        handleChange={changePasswordInput}
        err={passwordError}
        placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
        nameVisible={false}
        errVisible={false}
      />
      <SubmitButton onClick={handleSubmit} disabled={isLoading > 0}>{getBtnName('LOGIN')}</SubmitButton>
      <SubContainer>
        <SearchForgot />
        <ToggleText>
          <span>{getModalMessage('LOGIN_NO_ACCOUNT')}</span>
          <RegisterText onClick={openRegisterModal}>{getBtnName('REGISTER')}</RegisterText>
        </ToggleText>
      </SubContainer>
    </LoginComp>
  );
};

const LoginComp = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  padding: 17px 14px;
  outline: none;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3vh;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const ToggleText = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const RegisterText = styled.span`
  font-style: italic;
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: scale(1.05);
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
`;


export default Login;
